import React from 'react';
import PropTypes from 'prop-types';
import {gql} from 'apollo-boost';
import {Query} from 'react-apollo';
import {filterHeadline, filterHTML, filterResource} from "../../helpers/filters";
import Paragraph from "../Paragraph";

interface Data {
    paragraphById: {
        fieldTxtHeadline: string;
        fieldTxtSubTitle: string;
        fieldHtmlBody: Array<{
            value: string;
            format: string;
            processed: string;
        }>;
        fieldObjectImgMedia: Array<{
            entity: Array<{
                fieldMediaImage: Array<{
                    width: string;
                    height: string;
                    url: string;
                    targetId: string;
                    alt: string;
                    title: string;
                }>;
            }>;
        }>;
        fieldObjectFeaturedItemCta: Array<{targetRevisionId: string}>;
        fieldBackgroundColor: string;
        fieldTextAlignment: string;
        fieldTextColor: string;
        fieldImageBelowContent: boolean;
        fieldInlineImage: boolean;
    }
}

class InnerCardFeatureBoxQuery extends Query<Data, {id: string}> {}

const GET_COMPONENT = gql`
query paragraphById($id: String!) {
  paragraphById:paragraphRevisionById(id: $id) {
    ... on Paragraph {
      ... on Paragraph {
        ... on ParagraphInnerCardFeatureBox {
          fieldTxtHeadline
          fieldTxtSubTitle
          fieldHtmlBody {
            value
            format
            processed
          }
          fieldObjectImgMedia {
              entity {
                ... on MediaImage {
                  fieldMediaImage {
                    width
                    height
                    url
                    alt
                    title
                  }
                }
              }
          }
          fieldObjectFeaturedItemCta {
            targetRevisionId
          }
          fieldBackgroundColor
          fieldTextAlignment
          fieldTextColor
          fieldImageBelowContent
          fieldInlineImage
        }
      }
    }
  }
}
`;

const InnerCardFeatureBox = ({id, stylesObject, markComponentLoadedAs, handleLoadedComponents}) => (
    <InnerCardFeatureBoxQuery asyncMode query={GET_COMPONENT} variables={{ id }}>
        {({ loading, error, data }) => {
            if (loading) return null;
            if (error) {
                markComponentLoadedAs(id, true);
                return <div>{error}</div>;
            }

            const field = data.paragraphById;

            const imageBoxStyles = {};

            if (field.fieldObjectImgMedia && field.fieldObjectImgMedia.entity && field.fieldObjectImgMedia.entity.fieldMediaImage && field.fieldObjectImgMedia.entity.fieldMediaImage.url) {
                imageBoxStyles.background = `url('${filterResource(field.fieldObjectImgMedia.entity.fieldMediaImage.url)}') no-repeat center center`;
                imageBoxStyles.backgroundSize = 'cover';
            }

            let outerStyles = {};
            if (field.fieldBackgroundColor) {
                outerStyles.backgroundColor = field.fieldBackgroundColor;
            }
            if (field.fieldTextAlignment) {
                outerStyles.textAlign = field.fieldTextAlignment;
            }
            if (field.fieldTextColor) {
                outerStyles.color = field.fieldTextColor;
            }

            // Merge outerStyles and styles inherited from parent
            outerStyles = {...outerStyles, ...stylesObject};

            markComponentLoadedAs(id, true);

            const backgroundColorSet = Object.keys(outerStyles).includes('backgroundColor') ? !['#FFF', '#FFFFFF', '#fff', '#ffffff'].includes(outerStyles.backgroundColor) : false;

            return (
                <div className={`innerCardFeatureBox innerCardFeatureBox--${outerStyles.textAlign} innerCardFeatureBox--${backgroundColorSet ? 'withBg' : 'withoutBg'}`} style={outerStyles}>

                    {!field.fieldImageBelowContent && field.fieldObjectImgMedia && field.fieldObjectImgMedia.entity && field.fieldObjectImgMedia.entity.fieldMediaImage && field.fieldObjectImgMedia.entity.fieldMediaImage.url && (
                        <React.Fragment>
                            {field.fieldInlineImage ? (
                                <img className="innerCardFeatureBox__imageModuleInline"
                                     src={field.fieldObjectImgMedia.entity.fieldMediaImage.url}
                                     alt={field.fieldObjectImgMedia.entity.fieldMediaImage.alt}/>

                            ) : (
                                <div className="innerCardFeatureBox__imageModule" style={imageBoxStyles}/>
                            )}
                        </React.Fragment>
                    )}

                    <div className="innerCardFeatureBox__textModule">

                        {field.fieldTxtHeadline && (
                            // eslint-disable-next-line react/no-danger
                            <h2 className="innerCardFeatureBox__title" dangerouslySetInnerHTML={{__html: filterHeadline(field.fieldTxtHeadline)}} />
                        )}
                        {field.fieldTxtSubTitle && (
                            // eslint-disable-next-line react/no-danger
                            <h3 className="innerCardFeatureBox__subTitle" dangerouslySetInnerHTML={{__html: filterHeadline(field.fieldTxtSubTitle)}} />
                        )}

                        {field.fieldHtmlBody && field.fieldHtmlBody.processed && (
                            // eslint-disable-next-line react/no-danger
                            <div className="innerCardFeatureBox__body" dangerouslySetInnerHTML={{__html: filterHTML(field.fieldHtmlBody.processed)}}/>
                        )}

                        {field.fieldObjectFeaturedItemCta && field.fieldObjectFeaturedItemCta.targetRevisionId && (
                            <Paragraph key={`${id}_${field.fieldObjectFeaturedItemCta.targetRevisionId}`}
                                       id={String(field.fieldObjectFeaturedItemCta.targetRevisionId)}
                                       handleLoadedComponents={handleLoadedComponents}/>
                        )}
                    </div>

                    {field.fieldImageBelowContent && field.fieldObjectImgMedia && field.fieldObjectImgMedia.entity && field.fieldObjectImgMedia.entity.fieldMediaImage && field.fieldObjectImgMedia.entity.fieldMediaImage.url && (
                        <React.Fragment>
                            {field.fieldInlineImage ? (
                                <img className="innerCardFeatureBox__imageModuleInline"
                                     src={field.fieldObjectImgMedia.entity.fieldMediaImage.url}
                                     alt={field.fieldObjectImgMedia.entity.fieldMediaImage.alt}/>

                            ) : (
                                <div className="innerCardFeatureBox__imageModule" style={imageBoxStyles}/>
                            )}
                        </React.Fragment>
                    )}


                </div>
            );
        }}
    </InnerCardFeatureBoxQuery>
);

InnerCardFeatureBox.propTypes = {
    id: PropTypes.string.isRequired,
    // eslint-disable-next-line react/forbid-prop-types
    stylesObject: PropTypes.object,
    markComponentLoadedAs: PropTypes.func.isRequired,
    handleLoadedComponents: PropTypes.func.isRequired
};

InnerCardFeatureBox.defaultProps = {
    stylesObject: {}
};


export default InnerCardFeatureBox;



// WEBPACK FOOTER //
// ./frontend/components/FeatureCards/InnerCardFeatureBox.jsx